import { IImageProps, ITheme, Shimmer, useTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPImage, IPText } from 'baseComponents';
import { IPImageFit } from 'baseComponents/Image/IPImage';
import { IPStack } from 'baseComponents/Stack/IPStack';
import {
  globalBoxBorder,
  globalBoxBorderDarkMode,
  globalBoxBorderRadius,
  globalBoxShadow,
  globalFontBoldWeight,
  globalStackTokensGapSmall,
} from 'globalStyles';
import { useContext, useEffect, useState } from 'react';
import Config from 'services/Config/configService';
import { getProminentImageAccent } from 'utils/prominentColor';

interface IItemCardProps {
  image?: string;
  title: string;
  description?: string;
  children?: React.ReactNode;
  width?: number;
  height?: number;
  onClick?: () => void;
  stepNr?: number;
  loading?: boolean;
  //Fixed height for the title, in pixels. Cards standing next to each other whose titles wrap onto a different
  //number of lines otherwise start their content at a different height, which is visible when that content is a
  //single large number. Leave it out to let the title take the space it needs.
  titleHeight?: number;
}

export const getCardStyle = (
  useDarkMode: boolean,
  theme: ITheme,
  showHover: boolean,
  width?: number,
  height?: number,
) => {
  return {
    root: {
      border: useDarkMode ? globalBoxBorderDarkMode : globalBoxBorder,
      background: useDarkMode ? undefined : theme.palette.white,
      borderRadius: globalBoxBorderRadius,
      boxShadow: globalBoxShadow,
      paddingLeft: 10,
      paddingTop: 10,
      paddingBottom: 10,
      paddingRight: 10,
      height: width ?? 340,
      width: height ?? 220,
      '&:hover': showHover
        ? {
            background: useDarkMode ? theme.palette.themeDarker : theme.palette.themeLighterAlt,
            cursor: 'pointer',
          }
        : undefined,
      overflowX: 'hidden',
    },
  };
};

const ItemCard = (props: IItemCardProps) => {
  const appContext = useContext(AppContext);
  const theme = useTheme();
  const isDark = appContext.useDarkMode;
  const [startColor, setStartColor] = useState<string>(
    isDark ? theme.palette.themeDarker : theme.palette.themeLighterAlt,
  );
  const endColor = isDark ? '#000000' : '#FFFFFF';

  const stepNumber =
    props.stepNr !== undefined ? (
      <IPStack.Item
        styles={{
          root: {
            alignItems: 'center',
            background: theme.palette.themeLight,
            borderRadius: '50%',
            color: theme.palette.neutralPrimary,
            display: 'flex',
            flexShrink: 0,
            fontWeight: globalFontBoldWeight,
            height: 30,
            justifyContent: 'center',
            width: 30,
          },
        }}
      >
        {props.stepNr}
      </IPStack.Item>
    ) : undefined;

  const getImageProps = (): IImageProps => {
    return {
      src: `${Config.getImageURL()}/${props.image}`,
      imageFit: IPImageFit.contain,
      width: props.width ? props.width - 20 : 200, //must align with default width in getCardStyle + horizontal padding
      height: 60, //keep 2px top and bottom padding related to the image container (64 height in card style)
    };
  };

  // Load prominent color from image
  useEffect(() => {
    const loadColor = async () => {
      try {
        const imageUrl = getImageProps().src;
        if (imageUrl) {
          const result = await getProminentImageAccent(imageUrl);
          setStartColor(result.accentHex);
        }
      } catch {
        // fallback to theme color
        setStartColor(isDark ? theme.palette.themeDarker : theme.palette.themeLighterAlt);
      }
    };

    if (props.image) {
      loadColor();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.image, isDark]);

  return (
    <IPStack
      styles={getCardStyle(appContext.useDarkMode, theme, !!props.onClick && !props.loading, props.height, props.width)}
      onClick={props.loading ? undefined : props.onClick}
      tokens={globalStackTokensGapSmall}
    >
      {props.loading && (
        <IPStack.Item grow styles={{ root: { display: 'flex', alignItems: 'center' } }}>
          <IPStack tokens={globalStackTokensGapSmall} styles={{ root: { width: '100%' } }}>
            <Shimmer width="90%" />
            <IPStack tokens={globalStackTokensGapSmall} styles={{ root: { marginTop: 20 } }}>
              <Shimmer width="70%" />
              <Shimmer width="70%" />
              <Shimmer width="70%" />
            </IPStack>
          </IPStack>
        </IPStack.Item>
      )}
      {!props.loading && (
        <>
          {props.image && (
            <IPStack.Item
              styles={{
                root: {
                  height: 64,
                  alignContent: 'center',
                  background: `linear-gradient(135deg, ${startColor} 00%, ${endColor} 100%)`,
                  borderRadius: globalBoxBorderRadius,
                },
              }}
            >
              <IPImage {...getImageProps()} />
            </IPStack.Item>
          )}
          {props.stepNr !== undefined ? (
            <IPStack.Item grow>
              <IPStack horizontal verticalAlign="start" tokens={globalStackTokensGapSmall}>
                {stepNumber}
                <IPStack.Item grow>
                  <IPStack tokens={globalStackTokensGapSmall}>
                    <IPStack.Item styles={{ root: { height: props.titleHeight } }}>
                      <IPText
                        variant="mediumPlus"
                        styles={{ root: { wordBreak: 'break-word', fontWeight: globalFontBoldWeight } }}
                      >
                        {props.title}
                      </IPText>
                    </IPStack.Item>
                    {props.description && (
                      <IPStack.Item grow styles={{ root: { marginBottom: 5, overflow: 'hidden' } }}>
                        <IPText variant="small">{props.description}</IPText>
                      </IPStack.Item>
                    )}
                    {props.children && <IPStack.Item>{props.children}</IPStack.Item>}
                  </IPStack>
                </IPStack.Item>
              </IPStack>
            </IPStack.Item>
          ) : (
            <>
              <IPStack.Item styles={{ root: { height: props.titleHeight } }}>
                <IPText
                  variant="mediumPlus"
                  styles={{ root: { wordBreak: 'break-word', fontWeight: globalFontBoldWeight } }}
                >
                  {props.title}
                </IPText>
              </IPStack.Item>
              {props.description && (
                <IPStack.Item grow styles={{ root: { marginBottom: 5, overflow: 'hidden' } }}>
                  <IPText variant="small">{props.description}</IPText>
                </IPStack.Item>
              )}
              {/*with a description the description takes the free space, so the content sits at the bottom of every
                 card in a row whatever the description length; without one the content keeps the space itself*/}
              {props.children && <IPStack.Item grow={!props.description}>{props.children}</IPStack.Item>}
            </>
          )}
        </>
      )}
    </IPStack>
  );
};

export default ItemCard;
